import { role as roleEnum, RoleEnumKey } from '@jobilla/nexus-sdk'
import { useCallback, useSyncExternalStore } from 'react'

const STORAGE_KEY = 'debug:overrides'
const STORAGE_EVENT = 'debug:overrides-change'

const debugBarEnabled = import.meta.env.VITE_ENABLE_DEBUG_BAR === 'true'

/**
 * Add a new override by:
 *   1. extending this `DebugOverrides` interface with the typed value,
 *   2. adding a corresponding entry to `VALIDATORS` below.
 *
 * Consumers then use `useOverride('your-key')` / `useDebugOverride('your-key')`
 * and the rest of the pipeline (storage, header, reactivity) works
 * unchanged. The backend `DebugOverridesMiddleware` should be extended to
 * honour the new key.
 */
export interface DebugOverrides {
	role: RoleEnumKey
}

export type DebugOverrideKey = keyof DebugOverrides

type Validator<K extends DebugOverrideKey> = (raw: unknown) => DebugOverrides[K] | undefined

const VALIDATORS: { [K in DebugOverrideKey]: Validator<K> } = {
	role: (raw) => {
		if (typeof raw !== 'string') { return undefined }
		const known = Object.values(roleEnum) as ReadonlyArray<string>
		return known.includes(raw) ? (raw as RoleEnumKey) : undefined
	},
}

function readAll(): Partial<DebugOverrides> {
	if (!debugBarEnabled) { return {} }
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) { return {} }
		const parsed = JSON.parse(raw) as Record<string, unknown>
		const out: Partial<DebugOverrides> = {}
		for (const key of Object.keys(VALIDATORS) as DebugOverrideKey[]) {
			const validated = VALIDATORS[key](parsed[key])
			if (validated !== undefined) {
				out[key] = validated
			}
		}
		return out
	} catch {
		return {}
	}
}

function writeAll(next: Partial<DebugOverrides>): void {
	try {
		const filtered: Record<string, unknown> = {}
		for (const [key, value] of Object.entries(next)) {
			if (value !== undefined && value !== null) {
				filtered[key] = value
			}
		}
		if (Object.keys(filtered).length === 0) {
			localStorage.removeItem(STORAGE_KEY)
		} else {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
		}
	} catch {
		// localStorage unavailable — best-effort only.
	}
	window.dispatchEvent(new Event(STORAGE_EVENT))
}

// useSyncExternalStore compares snapshots with `Object.is`, so we have to hand
// it the same reference between events. We cache the most recently computed
// snapshot here and invalidate it whenever the store fires a change event.
let cachedSnapshot: Partial<DebugOverrides> | null = null

function subscribe(listener: () => void): () => void {
	const onChange = () => {
		cachedSnapshot = null
		listener()
	}
	window.addEventListener(STORAGE_EVENT, onChange)
	window.addEventListener('storage', onChange)
	return () => {
		window.removeEventListener(STORAGE_EVENT, onChange)
		window.removeEventListener('storage', onChange)
	}
}

function getSnapshot(): Partial<DebugOverrides> {
	if (cachedSnapshot === null) {
		cachedSnapshot = readAll()
	}
	return cachedSnapshot
}

const EMPTY_OVERRIDES: Partial<DebugOverrides> = {}

function getServerSnapshot(): Partial<DebugOverrides> {
	return EMPTY_OVERRIDES
}

/**
 * Reactive snapshot of every active debug override. Returns an empty object
 * when the debug bar is disabled.
 */
export function useDebugOverrides(): Partial<DebugOverrides> {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Reactive read of a single typed override. Returns `undefined` when the
 * override is not set, or when the debug bar is disabled.
 */
export function useOverride<K extends DebugOverrideKey>(key: K): DebugOverrides[K] | undefined {
	const all = useDebugOverrides()
	return all[key]
}

/**
 * Reactive read + setter for a single override. Persisted to localStorage;
 * setter is a no-op when the debug bar is disabled.
 */
export function useDebugOverride<K extends DebugOverrideKey>(key: K): {
	value: DebugOverrides[K] | undefined
	setValue: (next: DebugOverrides[K] | undefined) => void
} {
	const value = useOverride(key)
	const setValue = useCallback((next: DebugOverrides[K] | undefined) => {
		if (!debugBarEnabled) { return }
		writeAll({ ...readAll(), [key]: next })
	}, [key])
	return { value, setValue }
}

/**
 * Synchronous, hookless snapshot of all active overrides. Use from places
 * that can't call hooks (axios interceptors, route loaders, error handlers).
 * Returns an empty object when the debug bar is disabled.
 */
export function readOverrides(): Partial<DebugOverrides> {
	return readAll()
}
